/**
 * Utility functions for navigating to UnAssignFee component with URL parameters
 * Similar to collectFeeNavigation.js but for UnAssignFee component
 * 
 * Examples of usage:
 * 
 * 1. Navigate to specific student with organization:
 *    const url = generateUnAssignFeeURL({
 *      studentId: 'e153cdda-50c1-70ca-90d6-26b75330a1da',
 *      searchLevel: 2,
 *      orgId: 'org_12345'
 *    });
 *    navigate(url);
 * 
 * 2. Navigate with branch filter and organization:
 *    const url = generateUnAssignFeeURL({
 *      searchLevel: 1,
 *      boardId: 'board_1',
 *      branchId: 'branch_2',
 *      classId: 'class_3',
 *      sectionId: 'section_4',
 *      orgId: 'org_12345'
 *    });
 * 
 * 3. Navigate with student search value:
 *    const url = generateUnAssignFeeURL({
 *      searchLevel: 2,
 *      searchValue: 'John Doe',
 *      searchType: 'studentName',
 *      orgId: 'org_12345'
 *    });
 */

/**
 * Generate URL for UnAssignFee component with parameters
 * @param {Object} params - URL parameters
 * @param {string} params.studentId - Student ID
 * @param {number} params.searchLevel - Search level (1 or 2)
 * @param {string} params.orgId - Organization ID
 * @param {string} params.boardId - Board ID (for searchLevel 1)
 * @param {string} params.branchId - Branch ID (for searchLevel 1)
 * @param {string} params.classId - Class ID (for searchLevel 1)
 * @param {string} params.sectionId - Section ID (for searchLevel 1)
 * @param {string} params.searchValue - Search value (for searchLevel 2)
 * @param {string} params.searchType - Search type (studentName or enrollmentNumber)
 * @param {string} params.username - Username (for searchLevel 2)
 * @returns {string} Complete URL with query parameters
 */
export const generateUnAssignFeeURL = (params = {}) => {
    const searchParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== null && value !== undefined && value !== '') {
            searchParams.set(key, value);
        }
    });

    const search = searchParams.toString();
    return `/UnAssignFee${search ? `?${search}` : ''}`;
};

/**
 * Navigation examples for different scenarios
 */
export const UnAssignFeeNavigationExamples = {
    // Navigate to specific student with organization
    navigateToStudent: (studentId, orgId) => {
        return generateUnAssignFeeURL({
            studentId,
            searchLevel: 2,
            orgId
        });
    },

    // Navigate to branch filter with organization
    navigateToBranchFilter: (branchData, orgId) => {
        return generateUnAssignFeeURL({
            searchLevel: 1,
            orgId,
            ...branchData // { boardId, branchId, classId, sectionId }
        });
    },

    // Search for student with organization context
    navigateToStudentSearch: (searchValue, searchType = 'studentName', orgId) => {
        return generateUnAssignFeeURL({
            searchLevel: 2,
            searchValue,
            searchType,
            orgId
        });
    }
};

/**
 * Parse URL parameters for UnAssignFee component
 * @param {string} url - Full URL or search string
 * @returns {Object} Parsed parameters
 */
export const parseUnAssignFeeURL = (url) => {
    const searchParams = new URLSearchParams(url.includes('?') ? url.split('?')[1] : url);
    const params = {};

    for (let [key, value] of searchParams.entries()) {
        params[key] = value;
    }

    return params;
};

/**
 * Example usage in a React component:
 * 
 * import { generateUnAssignFeeURL, UnAssignFeeNavigationExamples } from '../utils/unAssignFeeNavigation';
 * import { useNavigate } from 'react-router-dom';
 * 
 * const MyComponent = () => {
 *   const navigate = useNavigate();
 *   const orgId = 'org_12345';
 * 
 *   const handleUnassignFee = (studentId) => {
 *     const url = UnAssignFeeNavigationExamples.navigateToStudent(studentId, orgId);
 *     navigate(url);
 *   };
 * 
 *   return (
 *     <div>
 *       <button onClick={() => handleUnassignFee('student_123')}>
 *         Unassign Fee
 *       </button>
 *     </div>
 *   );
 * };
 */

export default {
    generateUnAssignFeeURL,
    UnAssignFeeNavigationExamples,
    parseUnAssignFeeURL
};
